/**
 * shared/ui/toast.ts — sonner, with the fix attached (ef-1).
 *
 * Import this instead of `sonner` everywhere in the app. It is the same `toast`
 * object with two methods replaced: `error` and `warning` look their message up
 * in `shared/errors/fixes.ts` and, when the cause is one we know, add the
 * sentence that says what to do and a button that goes and does it.
 *
 * Why a wrapper and not a new helper everyone has to remember: Karr had 1,066
 * `toast.error` calls when this was written. A new helper reaches the ones
 * somebody rewrites; a wrapper reaches all of them the moment the import line
 * changes, and reaches every future one for free. `npm run check:ui:strict`
 * fails on a direct `import { toast } from 'sonner'` so it stays that way.
 *
 * A call site that already knows better always wins: pass your own `action` and
 * it is left alone.
 */
import { toast as sonnerToast } from 'sonner';
import type { ErrorFix } from '@/shared/errors/fixes';
import { fixesNow, loadFixes } from '@/shared/errors/fixesLazy';
import { canLinkToFix, goToFix } from '@/shared/errors/goToFix';
import { isChunkLoadError, reloadForFreshChunks } from '@/shared/utils/chunkReload';
import { reportError } from '@/shared/services/telemetry';
import { bareReasonSentence, isBareReason } from '../../../supabase/functions/_shared/upstreamError';

export { Toaster } from 'sonner';

type ToastArgs = Parameters<typeof sonnerToast.error>;
type ToastData = ToastArgs[1];

/** The fix for this toast, from the title or (more often) the description. */
function lookup(fixes: NonNullable<ReturnType<typeof fixesNow>>, message: unknown, data: ToastData): ErrorFix | null {
    return fixes.fixFor(message) ?? fixes.fixFor(data?.description);
}

/**
 * fetchfix - the description IS the raw browser transport error, whole.
 *
 * "Failed to fetch" is what the browser throws, not what a person reads; glued
 * to the fix line it produced "Failed to fetch Karr could not reach the
 * internet." on the founder's screen, 29 Aug 2026, mid-execute. When the whole
 * description is browser-speak, the fix sentence stands alone. Anchored to the
 * full string on purpose: a real sentence that merely CONTAINS one of these
 * phrases is somebody's actual message and is kept.
 */
const BROWSER_NOISE_RE =
    /^(typeerror: )?(failed to fetch|load failed|networkerror when attempting to fetch resource\.?|the (internet|network) connection (appears to be offline|was lost)\.?)$/i;

/**
 * errsay (founder, 24 Oct 2026): "That step did not go through / Unauthorized". A description that is
 * only a status word ("Unauthorized", "Forbidden", "HTTP 401") names no cause, so it is replaced by what
 * that status means and what to do. A title that is only a status word, with nothing under it, gets the
 * same sentence under it. Real sentences are never touched (`isBareReason` is anchored to the whole
 * string). The reader every edge call goes through does this first; this is the net under the rest.
 */
function sayWhatItMeans(message: ToastArgs[0], data?: ToastData): ToastData {
    const d = data?.description;
    if (typeof d === 'string' && d.trim() && isBareReason(d)) {
        return { ...data, description: bareReasonSentence({ message: d }) };
    }
    if (d === undefined && typeof message === 'string' && message.trim() && isBareReason(message)) {
        return { ...data, description: bareReasonSentence({ message }) };
    }
    return data;
}

function withFix(kind: 'error' | 'warning') {
    return (message: ToastArgs[0], given?: ToastData): string | number => {
        const data = sayWhatItMeans(message, given);
        // fetchfix - a stale chunk is not something to advise about, it is
        // something to HEAL: the global handler only sees imports that escape
        // to window, and an import awaited inside a click handler lands in the
        // caller's catch and arrives here instead. Start the same heal the
        // global path uses (budgeted, so this cannot loop); the fix sentence
        // below still shows for the moments before the reload, and stands on
        // its own if the budget is already spent.
        if (isChunkLoadError(message) || isChunkLoadError(data?.description)) {
            reloadForFreshChunks();
        }

        // notelen: the fix table loads after the first paint. An error before
        // it arrives shows at once, and the same toast takes its fix when it does.
        const fixes = fixesNow();
        if (!fixes) {
            const id = sonnerToast[kind](message, data);
            void loadFixes().then((f) => {
                if (lookup(f, message, data)) withFix(kind)(message, { ...data, id });
            }).catch(() => { /* the bare message stays */ });
            return id;
        }
        const fix = lookup(fixes, message, data);
        if (!fix) return sonnerToast[kind](message, data);

        const raw = typeof data?.description === 'string' ? data.description.trim() : '';
        const existing = BROWSER_NOISE_RE.test(raw) || isChunkLoadError(raw) ? '' : raw;
        // A description that does not end a sentence gets its full stop before
        // the fix line joins it, so the two read as prose, not a collision.
        const joined = existing && !/[.!?…]$/.test(existing) ? `${existing}.` : existing;
        const linkable = !!fix.to && canLinkToFix();
        return sonnerToast[kind](message, {
            ...data,
            // The cause first, then what to do about it. Both, never one.
            description: joined ? `${joined} ${fix.do}` : fix.do,
            action: data?.action ?? (linkable
                ? { label: fix.label ?? 'Fix this', onClick: () => { void goToFix(fix.to as string); } }
                : undefined),
            // Long enough to read a sentence and reach for the button. A fix
            // nobody can click in time is a fix nobody was given.
            duration: data?.duration ?? 10_000,
        });
    };
}

/**
 * redtoast (founder, 30 Oct 2026) - a red toast is recorded, not only shown.
 *
 * Only route crashes reached `client_errors`. A press that failed with a toast
 * ("Could not send the link") was seen by one person for ten seconds and by
 * nobody else, so `npm run errors` read a true zero while a client sat on the
 * signing modal and the founder heard "Karr chal hi nahi raha" on WhatsApp.
 * Now every `toast.error` goes into the same digest as kind `toast`, grouped
 * and counted like a crash, deduped per session by observability.ts.
 *
 * The text is scrubbed first: an error can quote the address a link went to,
 * a GSTIN, a PAN or a phone, and the digest is redacted on purpose
 * (tests/audit/errorDigestRedaction.test.ts).
 */
export function scrubToastText(text: string): string {
    return text
        .replace(/[^\s@<>()"',;]+@[^\s@<>()"',;]+\.[a-z]{2,}/gi, '<email>')
        .replace(/\b\d{2}[A-Z]{5}\d{4}[A-Z][0-9A-Z]Z[0-9A-Z]\b/g, '<gstin>')
        .replace(/\b[A-Z]{5}\d{4}[A-Z]\b/g, '<pan>')
        .replace(/\d{6,}/g, '<number>')
        .slice(0, 500);
}

function recordRedToast(message: ToastArgs[0], data?: ToastData): void {
    const title = typeof message === 'string' ? message.trim() : '';
    const d = typeof data?.description === 'string' ? data.description.trim() : '';
    const text = [title, d].filter(Boolean).join(' / ');
    if (!text) return;
    reportError(new Error(scrubToastText(text)), { kind: 'toast' });
}

const errorWithFix = withFix('error');

/**
 * `toast` is callable as well as an object of methods, so the wrapper has to be
 * both: the function body forwards, the own properties are copied, and only
 * error/warning are replaced.
 */
export const toast: typeof sonnerToast = Object.assign(
    ((...args: Parameters<typeof sonnerToast>) => sonnerToast(...args)) as typeof sonnerToast,
    sonnerToast,
    {
        error: (message: ToastArgs[0], data?: ToastData) => {
            recordRedToast(message, data);
            return errorWithFix(message, data);
        },
        warning: withFix('warning'),
    },
);
